import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { WorkOrder } from '../../types';
import { formatCurrency } from '../../utils/formatCurrency';
import { Table, Button, message, Card, Tooltip, Tag, Input, Modal, Space } from 'antd';
import {
    ArrowLeftOutlined,
    RotateLeftOutlined,
    SearchOutlined,
    ReloadOutlined,
    EyeOutlined
} from '@ant-design/icons';


const ArchivedWorkOrders: React.FC = () => {
    const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const navigate = useNavigate();
    const location = useLocation();

    // Ref to hold the timeout ID for debouncing
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchArchivedWorkOrders = async (search?: string) => {
        setLoading(true);
        try {
            const tokens = localStorage.getItem('cnf_auth_tokens');
            let accessToken = '';

            if (tokens) {
                const parsed = JSON.parse(tokens);
                accessToken = parsed.accessToken;
            }

            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

            let url = `${API_BASE_URL}/admin/work-orders/archived`;
            if (search) {
                url += `?search=${encodeURIComponent(search)}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setWorkOrders(data);
            } else {
                message.error('Failed to fetch archived work orders');
            }
        } catch (error) {
            console.error('Error fetching archived work orders:', error);
            message.error('Error connecting to server');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchArchivedWorkOrders();
        // Cleanup timeout on unmount
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchText(value);

        // Debounce logic
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            fetchArchivedWorkOrders(value);
        }, 500);
    };

    const handleUnarchive = async (workOrder: WorkOrder) => {
        try {
            const tokens = localStorage.getItem('cnf_auth_tokens');
            let accessToken = '';

            if (tokens) {
                const parsed = JSON.parse(tokens);
                accessToken = parsed.accessToken;
            }

            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
            const response = await fetch(`${API_BASE_URL}/admin/work-orders/${workOrder.id}/unarchive`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });

            if (response.ok) {
                message.success(`Work Order ${workOrder.wo_number} unarchived successfully`);
                fetchArchivedWorkOrders(searchText); // Refresh list with current search
            } else {
                message.error('Failed to unarchive work order');
            }
        } catch (error) {
            console.error('Error unarchiving work order:', error);
            message.error('Error connecting to server');
        }
    };

    const handleViewDetails = (workOrder: WorkOrder) => {
        // Get the current path to determine the role-based route
        const currentPath = location.pathname;
        let basePath = '/admin';

        if (currentPath.startsWith('/owner')) {
            basePath = '/owner';
        } else if (currentPath.startsWith('/advisor')) {
            basePath = '/advisor';
        } else if (currentPath.startsWith('/technician')) {
            basePath = '/technician';
        }

        navigate(`${basePath}/work-orders/${workOrder.id}`);
    };

    const columns = [
        {
            title: 'WO Number',
            dataIndex: 'wo_number',
            key: 'wo_number',
            render: (text: string) => <span style={{ fontWeight: 600 }}>{text}</span>,
            sorter: (a: WorkOrder, b: WorkOrder) => a.wo_number.localeCompare(b.wo_number),
        },
        {
            title: 'Customer',
            key: 'customer',
            render: (_: any, record: WorkOrder) => {
                if (record.customer) {
                    return record.customer.company_name || `${record.customer.first_name} ${record.customer.last_name}`;
                }
                return 'Unknown Customer';
            },
        },
        {
            title: 'Vehicle',
            key: 'vehicle',
            render: (_: any, record: WorkOrder) => {
                if (record.vehicle) {
                    return `${record.vehicle.year} ${record.vehicle.make} ${record.vehicle.model}`;
                }
                return 'Unknown Vehicle';
            }
        },
        {
            title: 'Date',
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: (date: string) => new Date(date).toLocaleDateString(),
            sorter: (a: WorkOrder, b: WorkOrder) => new Date(a.updated_at || '').getTime() - new Date(b.updated_at || '').getTime(),
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Total',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount: number) => formatCurrency(amount),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'completed' ? 'green' : 'default'}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: WorkOrder) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDetails(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Unarchive (Restore to active list)">
                        <Button
                            icon={<RotateLeftOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleUnarchive(record);
                            }}
                            size="small"
                        >
                            Unarchive
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/admin/work-orders')}
                    >
                        Back to Board
                    </Button>
                    <h1 style={{ margin: 0, fontSize: 24 }}>Archived Work Orders</h1>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Input
                        placeholder="Search archives..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={handleSearch}
                        style={{ width: 250 }}
                        allowClear
                    />
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchArchivedWorkOrders(searchText)}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            <Card>
                <Table
                    columns={columns}
                    dataSource={workOrders}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    onRow={(record) => ({
                        onClick: () => handleViewDetails(record),
                        style: { cursor: 'pointer' }
                    })}
                />
            </Card>


        </div>
    );
};

export default ArchivedWorkOrders;
